/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { authClientsAttachToMcpServer } from "../funcs/authClientsAttachToMcpServer.js";
import { authClientsCreate } from "../funcs/authClientsCreate.js";
import { authClientsDeleteIssuanceProfile } from "../funcs/authClientsDeleteIssuanceProfile.js";
import { authClientsDetachFromMcpServer } from "../funcs/authClientsDetachFromMcpServer.js";
import { authClientsGet } from "../funcs/authClientsGet.js";
import { authClientsGetAudience } from "../funcs/authClientsGetAudience.js";
import { authClientsGetMcpServerAuthentication } from "../funcs/authClientsGetMcpServerAuthentication.js";
import { authClientsList } from "../funcs/authClientsList.js";
import { authClientsListIssuanceProfiles } from "../funcs/authClientsListIssuanceProfiles.js";
import { authClientsListMcpServerChoices } from "../funcs/authClientsListMcpServerChoices.js";
import { authClientsSetAudience } from "../funcs/authClientsSetAudience.js";
import { authClientsSetHeaders } from "../funcs/authClientsSetHeaders.js";
import { authClientsSetIssuanceProfile } from "../funcs/authClientsSetIssuanceProfile.js";
import { authClientsSetMcpServerUpstreamAuthRequirement } from "../funcs/authClientsSetMcpServerUpstreamAuthRequirement.js";
import { authClientsUpdate } from "../funcs/authClientsUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { AuthClientAudience } from "../models/components/authclientaudience.js";
import { IssuanceProfile } from "../models/components/issuanceprofile.js";
import { ListIssuanceProfilesResponseBody } from "../models/components/listissuanceprofilesresponsebody.js";
import { ListMcpServerAuthClientChoicesResponseBody } from "../models/components/listmcpserverauthclientchoicesresponsebody.js";
import { McpServerAuthentication } from "../models/components/mcpserverauthentication.js";
import {
  AttachAuthClientToMcpServerRequest,
  AttachAuthClientToMcpServerSecurity,
} from "../models/operations/attachauthclienttomcpserver.js";
import {
  CreateAuthClientRequest,
  CreateAuthClientSecurity,
} from "../models/operations/createauthclient.js";
import {
  DeleteIssuanceProfileRequest,
  DeleteIssuanceProfileSecurity,
} from "../models/operations/deleteissuanceprofile.js";
import {
  DetachAuthClientFromMcpServerRequest,
  DetachAuthClientFromMcpServerSecurity,
} from "../models/operations/detachauthclientfrommcpserver.js";
import {
  GetAuthClientRequest,
  GetAuthClientSecurity,
} from "../models/operations/getauthclient.js";
import {
  GetAuthClientAudienceRequest,
  GetAuthClientAudienceSecurity,
} from "../models/operations/getauthclientaudience.js";
import {
  GetMcpServerAuthenticationRequest,
  GetMcpServerAuthenticationSecurity,
} from "../models/operations/getmcpserverauthentication.js";
import {
  ListAuthClientsRequest,
  ListAuthClientsResponse,
  ListAuthClientsSecurity,
} from "../models/operations/listauthclients.js";
import {
  ListIssuanceProfilesRequest,
  ListIssuanceProfilesSecurity,
} from "../models/operations/listissuanceprofiles.js";
import {
  ListMcpServerAuthClientChoicesRequest,
  ListMcpServerAuthClientChoicesSecurity,
} from "../models/operations/listmcpserverauthclientchoices.js";
import {
  SetAuthClientAudienceRequest,
  SetAuthClientAudienceSecurity,
} from "../models/operations/setauthclientaudience.js";
import {
  SetAuthClientHeadersRequest,
  SetAuthClientHeadersSecurity,
} from "../models/operations/setauthclientheaders.js";
import {
  SetIssuanceProfileRequest,
  SetIssuanceProfileSecurity,
} from "../models/operations/setissuanceprofile.js";
import {
  SetMcpServerUpstreamAuthRequirementRequest,
  SetMcpServerUpstreamAuthRequirementSecurity,
} from "../models/operations/setmcpserverupstreamauthrequirement.js";
import {
  UpdateAuthClientRequest,
  UpdateAuthClientSecurity,
} from "../models/operations/updateauthclient.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class AuthClients extends ClientSDK {
  /**
   * attachAuthClientToMcpServer authClients
   *
   * @remarks
   * Offer an existing auth client on another MCP server instead of creating a copy. The client stays one client: changing or rotating it changes upstream behavior on every server that offers it. Offering a client grants nobody permission to use it or access to the server. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
   */
  async attachToMcpServer(
    request: AttachAuthClientToMcpServerRequest,
    security?: AttachAuthClientToMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsAttachToMcpServer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createAuthClient authClients
   *
   * @remarks
   * Create an auth client and, optionally, offer it on MCP servers. A remote_session or service_account client authenticates through an existing OAuth registration whose credential owner matches its type; a static_credential or pass_through client authenticates with its own headers. Creating a client gives nobody permission to use it: set its audience to decide who may. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
   */
  async create(
    request: CreateAuthClientRequest,
    security?: CreateAuthClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteIssuanceProfile authClients
   *
   * @remarks
   * Delete an issuance profile. Sessions already issued keep their choices; later sessions decide theirs without it. Requires org:admin.
   */
  async deleteIssuanceProfile(
    request: DeleteIssuanceProfileRequest,
    security?: DeleteIssuanceProfileSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(authClientsDeleteIssuanceProfile(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * detachAuthClientFromMcpServer authClients
   *
   * @remarks
   * Stop an MCP server offering an auth client in its typed auth configuration. The client and its other servers are unchanged. Takes effect only where typed auth client resolution is enforced. It revokes no upstream credentials and does not remove the server's existing upstream OAuth association, which keeps serving elsewhere, including in shadow mode. Requires org:admin.
   */
  async detachFromMcpServer(
    request: DetachAuthClientFromMcpServerRequest,
    security?: DetachAuthClientFromMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsDetachFromMcpServer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getAuthClient authClients
   *
   * @remarks
   * Get an auth client in the caller's organization. Requires org:read.
   */
  async get(
    request: GetAuthClientRequest,
    security?: GetAuthClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getAuthClientAudience authClients
   *
   * @remarks
   * List who may use an auth client: the rules naming it and the broader rules that reach it. Requires org:read.
   */
  async getAudience(
    request: GetAuthClientAudienceRequest,
    security?: GetAuthClientAudienceSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClientAudience> {
    return unwrapAsync(authClientsGetAudience(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMcpServerAuthentication authClients
   *
   * @remarks
   * Get how an MCP server authenticates to its upstream: whether authentication is required and which auth clients it offers. Requires org:read.
   */
  async getMcpServerAuthentication(
    request: GetMcpServerAuthenticationRequest,
    security?: GetMcpServerAuthenticationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServerAuthentication> {
    return unwrapAsync(authClientsGetMcpServerAuthentication(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listAuthClients authClients
   *
   * @remarks
   * List the auth clients in the caller's organization. Requires org:read.
   */
  async list(
    request?: ListAuthClientsRequest | undefined,
    security?: ListAuthClientsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListAuthClientsResponse, { cursor: string }>> {
    return unwrapResultIterator(authClientsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listIssuanceProfiles authClients
   *
   * @remarks
   * List the issuance profiles for headless callers signing in through a sign-in. Requires org:read.
   */
  async listIssuanceProfiles(
    request: ListIssuanceProfilesRequest,
    security?: ListIssuanceProfilesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListIssuanceProfilesResponseBody> {
    return unwrapAsync(authClientsListIssuanceProfiles(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMcpServerAuthClientChoices authClients
   *
   * @remarks
   * List the shared upstream credentials an MCP server offers that a credential of the caller's, such as an agent key, can be set up to use. Requires access to connect to the server. Returns each choice's id, name and client_type, and whether the upstream requires authentication; no headers or credentials.
   */
  async listMcpServerChoices(
    request: ListMcpServerAuthClientChoicesRequest,
    security?: ListMcpServerAuthClientChoicesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListMcpServerAuthClientChoicesResponseBody> {
    return unwrapAsync(authClientsListMcpServerChoices(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setAuthClientAudience authClients
   *
   * @remarks
   * Replace the rules naming an auth client. Use grants mcp:auth on the client and Blocked writes mcp:blocked_auth; both apply on every server that offers the client. Server access is unchanged and still needs mcp:connect. Administering a client gives nobody permission to use it. Requires org:admin.
   */
  async setAudience(
    request: SetAuthClientAudienceRequest,
    security?: SetAuthClientAudienceSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClientAudience> {
    return unwrapAsync(authClientsSetAudience(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setAuthClientHeaders authClients
   *
   * @remarks
   * Replace the headers an auth client sends upstream. Rotating a shared client's credential changes upstream behavior on every server that offers it. Token-generating clients produce their own Authorization header, which cannot be configured. Clients created from a server's existing settings follow those settings and are edited there. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
   */
  async setHeaders(
    request: SetAuthClientHeadersRequest,
    security?: SetAuthClientHeadersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsSetHeaders(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setIssuanceProfile authClients
   *
   * @remarks
   * Create or replace the issuance profile of one headless caller signing in through a sign-in: an OAuth client, or a workload issuer and subject. Each choice names a credential the server offers, or none for a server that needs no upstream authentication. A workload subject represents no person, so its profile names shared credentials only. Choices take effect on sessions issued afterwards; existing sessions keep theirs. Choosing a credential grants nothing: each issuance checks the caller may use it. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
   */
  async setIssuanceProfile(
    request: SetIssuanceProfileRequest,
    security?: SetIssuanceProfileSecurity | undefined,
    options?: RequestOptions,
  ): Promise<IssuanceProfile> {
    return unwrapAsync(authClientsSetIssuanceProfile(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setMcpServerUpstreamAuthRequirement authClients
   *
   * @remarks
   * Return how an MCP server authenticates to its upstream. The requested requirement is ignored: whether the upstream needs authentication follows from the enabled auth clients the server offers. Not available for hosted toolsets. Requires org:admin.
   *
   * Deprecated: read getMcpServerAuthentication, and attach, detach, enable or disable auth clients instead.
   *
   * @deprecated method: This will be removed in a future release, please migrate away from it as soon as possible.
   */
  async setMcpServerUpstreamAuthRequirement(
    request: SetMcpServerUpstreamAuthRequirementRequest,
    security?: SetMcpServerUpstreamAuthRequirementSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServerAuthentication> {
    return unwrapAsync(authClientsSetMcpServerUpstreamAuthRequirement(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateAuthClient authClients
   *
   * @remarks
   * Rename, enable or disable an auth client. The change applies to the typed auth configuration of every MCP server that offers the client and takes effect only where typed auth client resolution is enforced. It revokes no upstream credentials; elsewhere, including in shadow mode, an MCP server's existing upstream configuration keeps serving. Requires org:admin.
   */
  async update(
    request: UpdateAuthClientRequest,
    security?: UpdateAuthClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
